import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Typography, Button, Input, Tag, Table, Space, Spin, message,
  Empty, Descriptions, List, Divider, Row, Col, Modal, Result, Statistic,
} from 'antd';
import {
  GiftOutlined, CheckCircleOutlined, CloseCircleOutlined,
  ClockCircleOutlined, FileTextOutlined, SendOutlined,
  ThunderboltOutlined, ReloadOutlined,
} from '@ant-design/icons';
import { couponRequestAPI } from '../services/api';

const { Title, Text, Paragraph } = Typography;

const statusConfig = {
  pending: { color: 'gold', icon: <ClockCircleOutlined />, label: '\u5F85\u5BE9\u6838' },
  approved: { color: 'green', icon: <CheckCircleOutlined />, label: '\u5DF2\u6838\u51C6' },
  rejected: { color: 'red', icon: <CloseCircleOutlined />, label: '\u5DF2\u62D2\u7D55' },
  expired: { color: 'default', icon: <ClockCircleOutlined />, label: '\u5DF2\u904E\u671F' },
};

export default function CouponRequestPage() {
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applyOpen, setApplyOpen] = useState(false);
  const [notes, setNotes] = useState('');
  const [applying, setApplying] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [reqRes, statsRes] = await Promise.all([
        couponRequestAPI.myRequests(),
        couponRequestAPI.myStats(),
      ]);
      setRequests(reqRes.data || []);
      setStats(statsRes.data);
    } catch (e) { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleApply = async () => {
    setApplying(true);
    try {
      await couponRequestAPI.apply(notes.trim() || undefined);
      message.success('\u7533\u8BF7\u5DF2\u9001\u51FA\uFF0C\u8BF7\u7B49\u5F85\u7BA1\u7406\u5458\u5BA1\u6838');
      setApplyOpen(false);
      setNotes('');
      fetchData();
    } catch (e) {
      message.error(e.message || '\u7533\u8BF7\u5931\u8D25');
    }
    setApplying(false);
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 80 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', maxWidth: 900, margin: '0 auto' }}>
      <Title level={3}><GiftOutlined /> {'\u512A\u60E0\u5238\u7533\u8BF7'}</Title>
      <Paragraph type="secondary">
        {'\u7533\u8BF7 AEO Scanner \u512A\u60E0\u5238\uFF0C\u5BA1\u6838\u901A\u8FC7\u540E\u5373\u53EF\u83B7\u5F97\u514D\u8D39\u626B\u63CF\u6B21\u6570\u3002'}
      </Paragraph>

      {/* Stats Cards */}
      {stats && (
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={8}>
            <Card>
              <Statistic
                title={'\u5269\u4F59\u626B\u63CF\u6B21\u6570'}
                value={stats.remaining_scans}
                prefix={<ThunderboltOutlined />}
                valueStyle={{ color: stats.remaining_scans > 0 ? '#52c41a' : '#999' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card>
              <Statistic
                title={'\u5DF2\u6838\u51C6'}
                value={stats.approved_count}
                prefix={<CheckCircleOutlined />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card>
              <Statistic
                title={'\u5F85\u5BA9\u6838'}
                value={stats.pending_count}
                prefix={<ClockCircleOutlined />}
                valueStyle={{ color: stats.pending_count > 0 ? '#faad14' : '#999' }}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* Apply Button */}
      <Card style={{ marginBottom: 24, textAlign: 'center', background: '#fafafa' }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>
          <GiftOutlined style={{ color: '#722ed1' }} />
        </div>
        <Title level={4}>{'\u8FD8\u6CA1\u6709\u514D\u8D39\u626B\u63CF\u6B21\u6570\uFF1F'}</Title>
        <Paragraph type="secondary">
          {'\u7533\u8BF7\u512A\u60E0\u5238\uFF0C\u5BA1\u6838\u901A\u8FC7\u540E\u5373\u53EF\u514D\u8D39\u83B7\u5F97 AEO Scanner \u626B\u63CF\u6B21\u6570\u3002'}
        </Paragraph>
        <Button
          type="primary"
          size="large"
          icon={<SendOutlined />}
          onClick={() => setApplyOpen(true)}
        >
          {'\u7533\u8BF7\u512A\u60E0\u5238'}
        </Button>
      </Card>

      {/* Request History */}
      <Card title={'\u7533\u8BF7\u8BB0\u5F55'}>
        {requests.length === 0 ? (
          <Empty description={'\u5C1A\u65E0\u7533\u8BF7\u8BB0\u5F55'} />
        ) : (
          <List
            dataSource={requests}
            renderItem={(item) => {
              const cfg = statusConfig[item.status] || { color: 'default', icon: <ClockCircleOutlined />, label: item.status };
              return (
                <List.Item>
                  <List.Item.Meta
                    avatar={<div style={{ fontSize: 24 }}>{cfg.icon}</div>}
                    title={
                      <Space>
                        <Tag color={cfg.color}>{cfg.label}</Tag>
                        {item.status === 'approved' && item.coupon_code && (
                          <Text code>{item.coupon_code}</Text>
                        )}
                      </Space>
                    }
                    description={
                      <div>
                        <Text type="secondary">
                          {'\u7533\u8BF7\u65F6\u95F4\uFF1A'}{item.created_at ? new Date(item.created_at).toLocaleString('zh-TW') : '-'}
                        </Text>
                        {item.notes && <div><Text>{'\u5907\u6CE8\uFF1A'}{item.notes}</Text></div>}
                        {item.status === 'approved' && (
                          <div style={{ marginTop: 4 }}>
                            <Tag color="blue">
                              {'\u5269\u4F59 '}{item.max_scans - item.used_scans}{' / '}{item.max_scans}{' \u6B21\u626B\u63CF'}
                            </Tag>
                            {item.approved_at && (
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                {'\u6838\u51C6\u65F6\u95F4\uFF1A'}{new Date(item.approved_at).toLocaleString('zh-TW')}
                              </Text>
                            )}
                          </div>
                        )}
                        {item.status === 'rejected' && item.admin_notes && (
                          <div style={{ marginTop: 4 }}>
                            <Text type="danger">{'\u7BA1\u7406\u5458\u5907\u6CE8\uFF1A'}{item.admin_notes}</Text>
                          </div>
                        )}
                      </div>
                    }
                  />
                </List.Item>
              );
            }}
          />
        )}
      </Card>

      {/* Apply Modal */}
      <Modal
        title={'\u7533\u8BF7\u512A\u60E0\u5238'}
        open={applyOpen}
        onCancel={() => { setApplyOpen(false); setNotes(''); }}
        footer={null}
      >
        <div style={{ padding: '8px 0' }}>
          <Paragraph>
            {'\u9001\u51FA\u7533\u8BF7\u540E\uFF0C\u7BA1\u7406\u5458\u4F1A\u5BA1\u6838\u4F60\u7684\u9700\u6C42\u3002\u5BA1\u6838\u901A\u8FC7\u540E\u4F60\u5C06\u6536\u5230 Email \u901A\u77E5\uFF0C\u5E76\u81EA\u52A8\u83B7\u5F97 AEO Scanner \u514D\u8D39\u626B\u63CF\u6B21\u6570\u3002'}
          </Paragraph>
          <div style={{ marginBottom: 16 }}>
            <Text>{'\u7533\u8BF7\u539F\u56E0\uFF08\u9009\u586B\uFF09\uFF1A'}</Text>
            <Input.TextArea
              rows={3}
              placeholder={'\u8BF7\u8BF4\u660E\u4E3A\u4EC0\u4E48\u9700\u8981 AEO Scanner \u512A\u60E0\u5238...'}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              style={{ marginTop: 8 }}
            />
          </div>
          <div style={{ textAlign: 'right', marginTop: 16 }}>
            <Button onClick={() => { setApplyOpen(false); setNotes(''); }} style={{ marginRight: 8 }}>
              {'\u53D6\u6D88'}
            </Button>
            <Button type="primary" loading={applying} onClick={handleApply}>
              {'\u9001\u51FA\u7533\u8BF7'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
